/**
 * Research-use attestation service (shop.research_use_attestations).
 *
 * Buyers attest "I am purchasing for research purposes only" once per
 * vendor. Stored with timestamp + SHA-256 hash of the originating IP
 * and user-agent. Vendors can force a re-attest per-product via
 * shop.products.requires_research_use_reattestation.
 */

import { supabase } from '../client';

export interface ShopResearchUseAttestation {
  id: string;
  buyer_user_id: string;
  vendor_id: string;
  attestation_version: number;
  attested_at: string;
  ip_hash: string | null;
  user_agent_hash: string | null;
  metadata: Record<string, unknown>;
}

/**
 * Has THIS buyer attested for THIS vendor at the current attestation
 * version (1)? Backed by a direct row lookup -- the RPC layer enforces
 * RLS so this is safe for client callers.
 */
export async function hasBuyerAttestedForVendor(input: {
  buyerUserId: string;
  vendorId: string;
}): Promise<boolean> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('research_use_attestations')
    .select('id')
    .eq('buyer_user_id', input.buyerUserId)
    .eq('vendor_id', input.vendorId)
    .eq('attestation_version', 1)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return !!data;
}

/**
 * Records an attestation. Idempotent on (buyer, vendor, version).
 * Pass `ip`/`userAgent` if the caller knows them -- they are SHA-256
 * hashed in the RPC, never persisted raw.
 */
export async function recordResearchUseAttestation(input: {
  vendorId: string;
  attestationVersion?: number;
  ip?: string;
  userAgent?: string;
}): Promise<ShopResearchUseAttestation> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_record_attestation', {
      p_vendor_id: input.vendorId,
      p_attestation_version: input.attestationVersion ?? 1,
      p_ip: input.ip ?? null,
      p_user_agent: input.userAgent ?? null,
    });
  if (error) throw error;
  return data as ShopResearchUseAttestation;
}

/**
 * Server-side decision: does this cart need a fresh attestation?
 * Wraps `shop.fn_buyer_attestation_required` so the client doesn't
 * have to re-derive the rule. Pass `cartId` to include the per-product
 * `requires_research_use_reattestation` flag.
 */
export async function isAttestationRequired(input: {
  buyerUserId: string;
  vendorId: string;
  cartId?: string;
}): Promise<boolean> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('fn_buyer_attestation_required', {
      p_buyer_user_id: input.buyerUserId,
      p_vendor_id: input.vendorId,
      p_cart_id: input.cartId ?? null,
    });
  if (error) throw error;
  return !!data;
}
